import React, { useState, useCallback, useMemo } from "react";
import {
  ClipOverlay,
  GradientBlurConfig,
  GradientBlurKeyframe,
  GradientDirection,
} from "../../../types";
import {
  createGradientBlurKeyframe,
  validateGradientKeyframes,
} from "../../../utils/gradient-blur/gradient-blur-calculator";
import { Slider } from "../../ui/slider";
import { Toggle } from "../../ui/toggle";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Trash2, Plus } from "lucide-react";
import ColorPicker from "react-best-gradient-color-picker";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";

interface GradientBlurPanelProps {
  localOverlay: ClipOverlay;
  handleStyleChange: (updates: Partial<ClipOverlay["styles"]>) => void;
  onGradientConfigChange: (config: GradientBlurConfig) => void;
}

const DIRECTION_OPTIONS: { value: GradientDirection; label: string }[] = [
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
];

/**
 * GradientBlurPanel Component
 *
 * Provides controls for adding and customizing gradient blur overlays on video overlays.
 * Supports multiple keyframed gradients with direction, color, and positioning controls.
 */
export const GradientBlurPanel: React.FC<GradientBlurPanelProps> = ({
  localOverlay,
  handleStyleChange,
  onGradientConfigChange,
}) => {
  const [selectedKeyframeId, setSelectedKeyframeId] = useState<string | null>(
    null
  );

  const gradientConfig = localOverlay.gradientBlur ?? {
    enabled: false,
    keyframes: [],
  };

  const selectedKeyframe = useMemo(() => {
    return gradientConfig.keyframes.find((kf) => kf.id === selectedKeyframeId);
  }, [gradientConfig.keyframes, selectedKeyframeId]);

  const handleToggle = (enabled: boolean) => {
    onGradientConfigChange({ ...gradientConfig, enabled });
  };

  const handleAddKeyframe = useCallback(() => {
    const startFrame = 0;
    const endFrame = localOverlay.durationInFrames;
    const newKeyframe = createGradientBlurKeyframe(startFrame, endFrame);

    const newKeyframes = [...gradientConfig.keyframes, newKeyframe].sort(
      (a, b) => a.startFrame - b.startFrame
    );

    const validation = validateGradientKeyframes(newKeyframes);
    if (!validation.valid) {
      console.warn("Cannot add gradient:", validation.error);
      return;
    }

    onGradientConfigChange({ ...gradientConfig, keyframes: newKeyframes });
    setSelectedKeyframeId(newKeyframe.id);
  }, [gradientConfig, onGradientConfigChange, localOverlay.durationInFrames]);

  const handleDeleteKeyframe = useCallback(
    (keyframeId: string) => {
      onGradientConfigChange({
        ...gradientConfig,
        keyframes: gradientConfig.keyframes.filter((kf) => kf.id !== keyframeId),
      });
      if (selectedKeyframeId === keyframeId) {
        setSelectedKeyframeId(null);
      }
    },
    [gradientConfig, onGradientConfigChange, selectedKeyframeId]
  );

  const handleUpdateKeyframe = useCallback(
    (keyframeId: string, updates: Partial<GradientBlurKeyframe>) => {
      const newKeyframes = gradientConfig.keyframes.map((kf) =>
        kf.id === keyframeId ? { ...kf, ...updates } : kf
      );

      const validation = validateGradientKeyframes(newKeyframes);
      if (!validation.valid) {
        console.warn("Invalid gradient update:", validation.error);
        return;
      }

      onGradientConfigChange({ ...gradientConfig, keyframes: newKeyframes });
    },
    [gradientConfig, onGradientConfigChange]
  );

  return (
    <div className="space-y-4 rounded-md bg-card p-4 border">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-extralight text-foreground">
          Gradient Blur
        </h3>
        <Toggle
          pressed={gradientConfig.enabled}
          onPressedChange={handleToggle}
          size="sm"
          className="text-xs text-foreground"
        >
          {gradientConfig.enabled ? "Disable" : "Enable"}
        </Toggle>
      </div>

      {gradientConfig.enabled && (
        <>
          {/* Gradient Keyframes List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-extralight">Gradients</Label>
              <Button
                onClick={handleAddKeyframe}
                size="sm"
                variant="outline"
                className="h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </div>

            {gradientConfig.keyframes.length > 0 ? (
              <div className="space-y-2">
                {gradientConfig.keyframes.map((keyframe, index) => (
                  <div
                    key={keyframe.id}
                    className={`flex items-center justify-between p-2 rounded-md border cursor-pointer transition-colors ${
                      selectedKeyframeId === keyframe.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => setSelectedKeyframeId(keyframe.id)}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded border"
                        style={{ backgroundColor: keyframe.color }}
                      />
                      <span className="text-xs">
                        Gradient {index + 1} - {keyframe.direction}
                      </span>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteKeyframe(keyframe.id);
                      }}
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground border rounded-md">
                <p className="text-xs">No gradients added yet</p>
              </div>
            )}
          </div>

          {/* Selected Keyframe Editor */}
          {selectedKeyframe && (
            <div className="space-y-3 rounded-md border p-3 bg-muted/30">
              <h4 className="text-xs font-medium">Edit Gradient</h4>

              {/* Direction Selector */}
              <div className="space-y-2">
                <Label className="text-xs font-extralight">Direction</Label>
                <Select
                  value={selectedKeyframe.direction}
                  onValueChange={(value) =>
                    handleUpdateKeyframe(selectedKeyframe.id, {
                      direction: value as GradientDirection,
                    })
                  }
                >
                  <SelectTrigger className="w-full text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIRECTION_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="text-xs"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <Label className="text-xs font-extralight">Color</Label>
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <div
                        className="h-8 w-8 rounded-md border cursor-pointer"
                        style={{ backgroundColor: selectedKeyframe.color }}
                      />
                    </PopoverTrigger>
                    <PopoverContent className="w-[330px] bg-card" side="right">
                      <ColorPicker
                        value={selectedKeyframe.color}
                        onChange={(color) =>
                          handleUpdateKeyframe(selectedKeyframe.id, { color })
                        }
                        hideHue
                        hideControls
                        hideColorTypeBtns
                        hideAdvancedSliders
                        hideColorGuide
                        hideInputType
                        height={200}
                      />
                    </PopoverContent>
                  </Popover>
                  <input
                    type="text"
                    value={selectedKeyframe.color}
                    onChange={(e) =>
                      handleUpdateKeyframe(selectedKeyframe.id, {
                        color: e.target.value,
                      })
                    }
                    placeholder="#000000"
                    className="flex-1 bg-background border rounded-md text-xs p-2 hover:border transition-colors text-primary"
                  />
                </div>
              </div>

              {/* Start Percent Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-extralight">Start %</Label>
                  <span className="text-xs min-w-[40px] text-right">
                    {selectedKeyframe.startPercent}%
                  </span>
                </div>
                <Slider
                  value={[selectedKeyframe.startPercent]}
                  onValueChange={(value) =>
                    handleUpdateKeyframe(selectedKeyframe.id, {
                      startPercent: value[0],
                    })
                  }
                  min={0}
                  max={100}
                  step={1}
                />
              </div>

              {/* End Percent Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-extralight">
                    End % (Softness)
                  </Label>
                  <span className="text-xs min-w-[40px] text-right">
                    {selectedKeyframe.endPercent}%
                  </span>
                </div>
                <Slider
                  value={[selectedKeyframe.endPercent]}
                  onValueChange={(value) =>
                    handleUpdateKeyframe(selectedKeyframe.id, {
                      endPercent: value[0],
                    })
                  }
                  min={0}
                  max={100}
                  step={1}
                />
              </div>

              {/* Opacity Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-extralight">Opacity</Label>
                  <span className="text-xs min-w-[40px] text-right">
                    {selectedKeyframe.opacity}%
                  </span>
                </div>
                <Slider
                  value={[selectedKeyframe.opacity]}
                  onValueChange={(value) =>
                    handleUpdateKeyframe(selectedKeyframe.id, {
                      opacity: value[0],
                    })
                  }
                  min={0}
                  max={100}
                  step={1}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
